import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import Heading from "../../components/typography/Heading";
import Text from "../../components/typography/Text";
import TextField from "../../components/input/textfield/TextField";
import Button from "../../components/buttons/Button";
import StatusAlert from "../../components/alerts/StatusAlert";
import FeedbackStatus from "../../components/feedback/FeedbackStatus";

import { useSignIn } from "../../features/auth/hooks/useSignIn";

import "./LandingPage.css";

export default function LandingPage() {
  const navigate = useNavigate();
  const { formData, status, handleChange, handleSubmit } = useSignIn();

  useEffect(() => {
    document.title = "Katch | Stream Share Connect";
  }, []);

  if (status.isUnverified) {
    return (
      <div className="home-page__verification">
        <FeedbackStatus
          title="Verify your email"
          description={
            <span>
              We've resent a verification link to{" "}
              <strong>{formData.email || "your email"}</strong>. Check your
              inbox or spam folder.
            </span>
          }
          buttonText="Back to Sign in"
          onAction={() => navigate("/sign-in")}
        />
      </div>
    );
  }

  return (
    <div className="home-page">
      <main className="home-page__main">
        <section className="home-page__hero">
          <div className="home-page__hero-content">
            <Text as="span" className="home-page__eyebrow">
              THE PLATFORM POWERING SPORTS CONTENT
            </Text>

            <Heading as="h1" size="xl" className="home-page__hero-title">
              Stream. Share.
              <span> Connect.</span>
            </Heading>

            <Text
              as="p"
              size="md"
              tone="secondary"
              className="home-page__hero-description"
            >
              Stream videos, share moments and connect with your sports
              community.
            </Text>
          </div>
        </section>

        <section className="home-page__auth">
          <div className="home-page__auth-content">
            <div className="home-page__auth-header">
              <Heading as="h2" size="lg">
                Welcome back
              </Heading>

              <Text as="p" size="sm" tone="secondary">
                Sign in to your Katch account.
              </Text>
            </div>

            <form
              className="home-page__form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!status.loading) handleSubmit();
              }}
            >
              {status.message && <StatusAlert status={status} />}

              <div className="home-page__fields">
                <TextField
                  name="email"
                  type="email"
                  label="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  autoComplete="email"
                  required
                />

                <TextField
                  name="password"
                  type="password"
                  label="Password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  showPasswordOption
                  required
                />
              </div>

              <div className="home-page__forgot">
                <Link to="/forgot-password" className="home-page__link">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                value={status.loading ? "Signing in..." : "Sign in"}
                variant="primary"
                disabled={status.loading}
              />
            </form>

            <Text
              as="p"
              size="sm"
              tone="secondary"
              className="home-page__register"
            >
              Don't have an account?{" "}
              <Link to="/sign-up" className="home-page__link">
                Create account
              </Link>
            </Text>
          </div>
        </section>
      </main>

      <footer className="home-page__footer">
        <Text as="span" size="xs" tone="secondary">
          © 2026 Katch
        </Text>

        <nav className="home-page__footer-nav" aria-label="Footer navigation">
          <Link to="/about">About</Link>
          <Link to="/features">Features</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/support">Support</Link>
        </nav>
      </footer>
    </div>
  );
}
